import type { LocaleObject } from '@nuxtjs/i18n'
import { BGN, CZK, EUR, GBP, HUF, PLN, RON, UAH, USD } from './currencies'

type RemoveIndex<T> = {
  [K in keyof T as string extends K ? never : number extends K ? never : symbol extends K ? never : K]: T[K]
}

type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] }

export interface StrictLocaleObject extends WithRequired<RemoveIndex<LocaleObject>, 'name' | 'dir' | 'language' | 'file'> {
  country: string
  currency: string
  flag: `circle-flags:${string}`
  borders: string[]
}

export const at: StrictLocaleObject = {
  code: 'at',
  language: 'de-AT',
  name: 'German',
  dir: 'ltr',
  file: 'de.json',
  country: 'AT',
  currency: EUR.currency,
  flag: 'circle-flags:at',
  borders: ['CZ', 'DE', 'HU', 'IT', 'LI', 'SK', 'SI', 'CH'],
}

export const bg: StrictLocaleObject = {
  code: 'bg',
  language: 'bg-BG',
  name: 'Bulgarian',
  dir: 'ltr',
  file: 'bg.json',
  country: 'BG',
  currency: BGN.currency,
  flag: 'circle-flags:bg',
  borders: ['GR', 'MK', 'RO', 'RS', 'TR'],
}

export const ch: StrictLocaleObject = {
  code: 'ch',
  language: 'de-CH',
  name: 'German',
  dir: 'ltr',
  file: 'de.json',
  country: 'CH',
  currency: 'CHF',
  flag: 'circle-flags:ch',
  borders: ['AT', 'FR', 'IT', 'LI', 'DE'],
}

export const cs: StrictLocaleObject = {
  code: 'cs',
  language: 'cs-CZ',
  name: 'Czech',
  dir: 'ltr',
  file: 'cs.json',
  country: 'CZ',
  currency: CZK.currency,
  flag: 'circle-flags:cz',
  borders: ['AT', 'DE', 'PL', 'SK'],
}

export const da: StrictLocaleObject = {
  code: 'da',
  language: 'da-DK',
  name: 'Danish',
  dir: 'ltr',
  file: 'da.json',
  country: 'DK',
  currency: 'DKK',
  flag: 'circle-flags:dk',
  borders: ['DE'],
}

export const de: StrictLocaleObject = {
  code: 'de',
  language: 'de-DE',
  name: 'German',
  dir: 'ltr',
  file: 'de.json',
  country: 'DE',
  currency: EUR.currency,
  flag: 'circle-flags:de',
  borders: ['AT', 'BE', 'CZ', 'DK', 'FR', 'LU', 'NL', 'PL', 'CH'],
}

export const en: StrictLocaleObject = {
  code: 'en',
  language: 'en-US',
  name: 'English',
  dir: 'ltr',
  file: 'en.json',
  country: 'US',
  currency: USD.currency,
  flag: 'circle-flags:us',
  borders: ['CA', 'MX'],
}

export const es: StrictLocaleObject = {
  code: 'es',
  language: 'es-ES',
  name: 'Spanish',
  dir: 'ltr',
  file: 'es.json',
  country: 'ES',
  currency: EUR.currency,
  flag: 'circle-flags:es',
  borders: ['AD', 'FR', 'GI', 'PT', 'MA'],
}

export const fr: StrictLocaleObject = {
  code: 'fr',
  language: 'fr-FR',
  name: 'French',
  dir: 'ltr',
  file: 'fr.json',
  country: 'FR',
  currency: EUR.currency,
  flag: 'circle-flags:fr',
  borders: ['AD', 'BE', 'DE', 'IT', 'LU', 'MC', 'ES', 'CH'],
}

export const gb: StrictLocaleObject = {
  code: 'gb',
  language: 'en-GB',
  name: 'English',
  dir: 'ltr',
  file: 'en.json',
  country: 'GB',
  currency: GBP.currency,
  flag: 'circle-flags:gb',
  borders: ['IE'],
}

export const hu: StrictLocaleObject = {
  code: 'hu',
  language: 'hu-HU',
  name: 'Hungarian',
  dir: 'ltr',
  file: 'hu.json',
  country: 'HU',
  currency: HUF.currency,
  flag: 'circle-flags:hu',
  borders: ['AT', 'HR', 'RO', 'RS', 'SK', 'SI', 'UA'],
}

export const it: StrictLocaleObject = {
  code: 'it',
  language: 'it-IT',
  name: 'Italian',
  dir: 'ltr',
  file: 'it.json',
  country: 'IT',
  currency: EUR.currency,
  flag: 'circle-flags:it',
  borders: ['AT', 'FR', 'SM', 'SI', 'CH', 'VA'],
}

export const nl: StrictLocaleObject = {
  code: 'nl',
  language: 'nl-NL',
  name: 'Dutch',
  dir: 'ltr',
  file: 'nl.json',
  country: 'NL',
  currency: EUR.currency,
  flag: 'circle-flags:nl',
  borders: ['BE', 'DE'],
}

export const pl: StrictLocaleObject = {
  code: 'pl',
  language: 'pl-PL',
  name: 'Polish',
  dir: 'ltr',
  file: 'pl.json',
  country: 'PL',
  currency: PLN.currency,
  flag: 'circle-flags:pl',
  borders: ['BY', 'CZ', 'DE', 'LT', 'RU', 'SK', 'UA'],
}

export const ro: StrictLocaleObject = {
  code: 'ro',
  language: 'ro-RO',
  name: 'Romanian',
  dir: 'ltr',
  file: 'ro.json',
  country: 'RO',
  currency: RON.currency,
  flag: 'circle-flags:ro',
  borders: ['BG', 'HU', 'MD', 'RS', 'UA'],
}

export const sk: StrictLocaleObject = {
  code: 'sk',
  language: 'sk-SK',
  name: 'Slovak',
  dir: 'ltr',
  file: 'sk.json',
  country: 'SK',
  currency: EUR.currency,
  flag: 'circle-flags:sk',
  borders: ['AT', 'CZ', 'HU', 'PL', 'UA'],
}

export const uk: StrictLocaleObject = {
  code: 'uk',
  language: 'uk-UA',
  name: 'Ukrainian',
  dir: 'ltr',
  file: 'uk.json',
  country: 'UA',
  currency: UAH.currency,
  flag: 'circle-flags:ua',
  borders: ['BY', 'HU', 'MD', 'PL', 'RO', 'RU', 'SK'],
}
